import {
  envIsIntegration,
  envIsServerDeployment,
  serverEnableCoreWeaveObservability,
  serverEnableSemanticLegend,
} from '../../config';
import {RampIdType} from '../../generated/graphql';
import {type Viewer} from '../../state/viewer/types';
import {setAndOrFiltersEnabled} from '../andOrFiltersFeatureFlag';
import {MAX_PINNED_RUNS, OLD_MAX_PINNED_RUNS} from '../constants';
import {getDatadogRum} from '../datadog/datadogRum';
import {setParallelGzipEnabled} from '../parallelGzipFeatureFlag';
import {
  setSparseCompressionEnabled,
  setSparseOverridesEnabled,
} from '../sparseOverridesFeatureFlag';
import {
  checkEntityRampFlag,
  useEntityRampFlag,
  useEntityRampFlagWithLoading,
  useOrgIdRampFlag,
  useOrgNameRampFlag,
  useOrgRampFlag,
  useOrgRampFlagWithLoading,
  useUserOrOrgRampFlag,
  useUserRampFlag,
  useUserRampFlagWithLoading,
} from '../useRampFlag';

const DD_RUM = getDatadogRum();
/*
Docs for ramp feature flags:
https://www.notion.so/wandbai/Ramp-Feature-Flag-Documentation-da56991072df4c8d980cd36804f52ded
*/

/**
 * This is a little hacky but we're wanting to safely prevent special orgs from getting
 * force enrolled in full fidelity until we're ready.
 * 1. The entity name check won't always work because not all workspaces are under namespaces like NVIDIA
 * 2. The `userInfo.company` field is not always populated
 * 3. viewer orgs is pretty reliable, so between all three of these this should be a comprehensive block
 */
// https://weightsandbiases.slack.com/archives/C062UKHTAHG/p1727116018580919
const BLOCK_LIST = ['OPENAI', 'NVIDIA'];
export const getShouldBlockEntity = (
  entityName: string,
  viewer: Viewer | undefined
) => {
  // block special entities
  const isEntityNameBlocked = BLOCK_LIST.includes(entityName.toUpperCase());

  // block based on viewer org
  const isViewerOrgBlocked = viewer?.organizations?.some(org => {
    return BLOCK_LIST.includes(org.name.toUpperCase());
  });

  // block based on userInfo.company
  const company = viewer?.userInfo?.company ?? '';
  const isCompanyBlocked = BLOCK_LIST.includes(company.toUpperCase());

  return isEntityNameBlocked || isViewerOrgBlocked || isCompanyBlocked;
};

export const useRampFlagDisableFuzzySearch = (entityName: string) => {
  const rampFlag = useEntityRampFlag(
    entityName,
    'disable-fuzzy-search',
    RampIdType.EntityName
  );
  if (envIsServerDeployment) {
    return true;
  }
  return rampFlag;
};

export const useRampFlagDisableQueryMerging = (entityName: string) => {
  return useEntityRampFlag(
    entityName,
    'disable-query-merging',
    RampIdType.EntityName
  );
};

export const useRampFlagDisableUpdateHistoryKeysInfo = () => {
  return useUserOrOrgRampFlag(
    'disable-update-history-key-info-user',
    'disable-update-history-key-info-org'
  );
};

export const checkRampFlagEnableOPFSCache = async (
  entityName: string
): Promise<boolean> => {
  return await checkEntityRampFlag(entityName, 'enable-opfs-cache');
};

export const useRampFlagEnableOPFSCache = (entityName: string) => {
  return useEntityRampFlag(
    entityName,
    'enable-opfs-cache',
    RampIdType.EntityName
  );
};

// in W&B server yet
export const useRampFlagAccountSelector = () => {
  if (envIsServerDeployment) {
    return false;
  }
  return true;
};

export const useRampFlagNoKeysets = () => {
  return useUserOrOrgRampFlag('no-keysets-user', 'no-keysets-org');
};

export const useRampFlagInferenceCatalog = (orgName: string) => {
  const ret = useOrgNameRampFlag(
    orgName,
    'inference-catalog',
    RampIdType.OrgName
  );

  // disabling for server
  if (envIsServerDeployment) {
    return false;
  }

  const rampFlag = !ret.loading && ret.data;

  return rampFlag ?? false;
};

// not all scenarios have an entity name - in those cases, it's
// fine to rely on the org for the flag check
export const useRampFlagMetricChangeNotifications = (
  orgName: string,
  entityName: string
) => {
  const entityEnabled = useEntityRampFlag(
    entityName,
    'run-metric-notifications-entity',
    RampIdType.EntityName
  );

  const orgEnabled = useOrgNameRampFlag(
    orgName,
    'run-metric-notifications',
    RampIdType.OrgName
  );
  if (orgEnabled.loading) {
    return {data: false, loading: true};
  }

  // We OR because the org flag is how we individually pick orgs to enable,
  // and the entity enable is for broadly allowing any paid entity.
  // Right now we don't see a need to disable orgs for this flag.
  return {data: entityEnabled || orgEnabled.data, loading: false};
};

export const useRampFlagStarredProjects = () => {
  return useUserRampFlag('starred-projects', RampIdType.UserName);
};

export const useRampFlagHideSummaryHistograms = (entityName: string) => {
  return useEntityRampFlag(
    entityName,
    'hide-summary-histograms',
    RampIdType.EntityName
  );
};

export const useRampFlagGQLLongTermCache = (entityName: string) => {
  const isUserEnabled = useUserRampFlag(
    'gql-long-term-cache-user',
    RampIdType.UserName
  );
  const isEntityEnabled = useEntityRampFlag(
    entityName,
    'gql-long-term-cache-entity',
    RampIdType.EntityName
  );
  return isUserEnabled || isEntityEnabled;
};

export const useRampFlagOrgLevelServiceAccounts = (orgName: string) => {
  return useOrgNameRampFlag(
    orgName,
    'org-level-service-accounts',
    RampIdType.OrgName
  );
};

export const useRampFlagEmbeddedPanels = (entityName: string) => {
  const isUserEnabled = useUserRampFlag(
    'embedded-panels-user',
    RampIdType.UserName
  );
  const isEntityEnabled = useEntityRampFlag(
    entityName,
    'embedded-panels-entity',
    RampIdType.EntityName
  );
  if (envIsServerDeployment || envIsIntegration) {
    return false;
  }
  return isUserEnabled || isEntityEnabled;
};

export const useRampFlagSemanticLegend = (entityName: string) => {
  const isOrgEnabled = useOrgRampFlag(
    'semantic-legend-org',
    RampIdType.OrgName
  );
  const isUserEnabled = useUserRampFlag(
    'semantic-legend-user',
    RampIdType.UserName
  );
  const isEntityEnabled = useEntityRampFlag(
    entityName,
    'semantic-legend-entity',
    RampIdType.EntityName
  );
  if (envIsServerDeployment) {
    return serverEnableSemanticLegend();
  }
  return isOrgEnabled || isUserEnabled || isEntityEnabled;
};

export const useRampFlagTeamReadmeSection = () =>
  useUserRampFlag('team-readme-section', RampIdType.UserName);

export const useRampFlagMetricsSearchAPI = (entityName: string) => {
  const userFlag = useUserRampFlag(
    'metrics-search-api-user',
    RampIdType.UserName
  );
  const entityFlag = useEntityRampFlag(
    entityName,
    'metrics-search-api-entity',
    RampIdType.EntityName
  );
  return userFlag || entityFlag;
};

export const useRampFlagIssuesObservability = (entityName: string) => {
  const userOrOrgFlag = useUserOrOrgRampFlag(
    'issues-observability-user',
    'issues-observability-org'
  );
  const entityFlag = useEntityRampFlag(
    entityName,
    'issues-observability-entity',
    RampIdType.EntityName
  );

  if (envIsServerDeployment) {
    return serverEnableCoreWeaveObservability();
  }
  const value = userOrOrgFlag || entityFlag;
  DD_RUM.setGlobalContextProperty('issues_observability', value);
  return value;
};

export const useStatsigIssuesObservability = (orgId: string) => {
  const value = useOrgIdRampFlag(
    'coreweave_observability',
    RampIdType.OrgId,
    orgId
  );

  DD_RUM.setGlobalContextProperty('coreweave_observability', value);
  return value;
};

export const useRampFlagMediaPanelTracking = (entityName: string) => {
  const isEntityEnabled = useEntityRampFlag(
    entityName,
    'media-panel-tracking-entity',
    RampIdType.EntityName
  );
  const isUserEnabled = useUserRampFlag(
    'media-panel-tracking-user',
    RampIdType.UserName
  );
  const isOrgEnabled = useOrgRampFlag(
    'media-panel-tracking-org',
    RampIdType.OrgName
  );
  return isEntityEnabled || isUserEnabled || isOrgEnabled;
};

export const useRampFlagMediaPanelTrackingDebug = (entityName: string) => {
  const isEntityEnabled = useEntityRampFlag(
    entityName,
    'media_panel_tracking_debug_entity',
    RampIdType.EntityName
  );
  const isUserEnabled = useUserRampFlag(
    'media_panel_tracking_debug_user',
    RampIdType.UserName
  );
  return isEntityEnabled || isUserEnabled;
};

export const useRampFlagShowOrgActivityPage = () => {
  const isOrgEnabled = useOrgRampFlag(
    'show_org_activity_page',
    RampIdType.OrgId
  );

  return isOrgEnabled || false;
};

export const useStatsigGateCoreWeaveSaasByob = (
  organizationId?: string | null
) => {
  // Only used for Saas as setting up CoreWeave buckets in server is a different process
  const isOrgEnabled = useOrgIdRampFlag(
    'saas_coreweave_storage_byob',
    RampIdType.OrgId,
    organizationId
  );

  if (envIsServerDeployment) {
    return false;
  }
  return isOrgEnabled;
};

export const useRampInferenceAsProduct = () => {
  return useOrgRampFlag('inference_as_product', RampIdType.OrgName);
};

export const useRampProPlanMonthlyOption = () => {
  const isUserEnabled = useUserRampFlag(
    'pro_plan_monthly_option',
    RampIdType.UserId
  );

  // never show for dedicated
  if (envIsServerDeployment) {
    return false;
  }

  return isUserEnabled;
};

export const useRampFlagFullScreenLinePlotEditing = (entityName: string) => {
  const isEntityEnabled = useEntityRampFlag(
    entityName,
    'fullscreen_line_plot_editing_entity',
    RampIdType.EntityName
  );
  const isUserEnabled = useUserRampFlag(
    'fullscreen_line_plot_editing_user',
    RampIdType.UserName
  );
  if (envIsIntegration || envIsServerDeployment) {
    return false;
  }
  return isEntityEnabled || isUserEnabled;
};

export const useRampFlagBRSDQWithoutRunInfo = (entityName: string) => {
  const {isEnabled: isUserEnabled, loading: isUserEnabledLoading} =
    useUserRampFlagWithLoading(
      'brsdq_without_run_info_user',
      RampIdType.UserId
    );
  const {data: isEntityEnabled, loading: isEntityEnabledLoading} =
    useEntityRampFlagWithLoading(
      entityName,
      'brsdq_without_run_info_entity',
      RampIdType.EntityId
    );
  const {data: isOrgEnabled, loading: isOrgEnabledLoading} =
    useOrgRampFlagWithLoading('brsdq_without_run_info_org', RampIdType.OrgId);
  if (isUserEnabledLoading || isEntityEnabledLoading || isOrgEnabledLoading) {
    return {
      isEnabled: undefined,
      loading: true,
    };
  }
  const definedFlags = [isUserEnabled, isEntityEnabled, isOrgEnabled].filter(
    flag => flag !== undefined
  );
  return {
    isEnabled: definedFlags.some(flag => flag),
    loading: false,
  };
};

export const useStatsigGateSourceArtifactProtection = (
  organizationName: string
): {data: boolean; loading: boolean} => {
  const {data, loading} = useOrgNameRampFlag(
    organizationName,
    'source_artifact_protection',
    RampIdType.OrgName
  );

  return {data, loading};
};

export const useStatsigGateAutomationHubFrontend = (
  organizationName: string
): {enabled: boolean; loading: boolean} => {
  const {data, loading} = useOrgNameRampFlag(
    organizationName,
    'automation_hub',
    RampIdType.OrgName
  );

  return {
    enabled: (!loading && data) || false,
    loading,
  };
};

export const useStatsigGateAutomationExecutionHistory = (
  organizationId?: string | null
) => {
  return useOrgIdRampFlag(
    'automation_execution_history',
    RampIdType.OrgId,
    organizationId
  );
};

export const useStatsigGateAutomationFree = (organizationId: string | null) => {
  /*
    Automations is now free for everyone
    Leaving in case we do want to rollback
  */
  return true;
};

export const useStatsigGateAutomationEntityScope = (
  organizationId?: string | null
) => {
  return useOrgIdRampFlag(
    'automation_entity_scope',
    RampIdType.OrgId,
    organizationId
  );
};

export const useRampFlagWeaveDebouncedScoring = () => {
  return useOrgIdRampFlag('weave_debounced_scoring', RampIdType.OrgId);
};

export const useRampFlagWeaveRemoteScoring = () => {
  return useOrgIdRampFlag('weave_remote_scoring', RampIdType.OrgId);
};

export const useStatsigGateBulkUserInvitations = () => {
  return useOrgIdRampFlag('bulk_org_invitations', RampIdType.OrgId);
};

export const useStatsigGateWeaveTraceAnalysisFrontend = () => {
  return useOrgIdRampFlag('weave_trace_analysis_frontend', RampIdType.OrgId);
};

export const useRampFlagOrgAlertsTab = () => {
  const statsigOrgRampFlag = useOrgRampFlag('org_alerts_tab', RampIdType.OrgId);
  if (envIsServerDeployment) {
    return true;
  }
  return statsigOrgRampFlag ?? false;
};

export const useStatsigGateSemanticLegendsBucketEditing = () => {
  return useOrgRampFlag(
    'semantic_legend_bucket_editing_org',
    RampIdType.OrgName
  );
};

export const useStatsigGateCollectionOverviewPhase1WithLoading = (
  orgId: string
): {enabled: boolean; loading: boolean} => {
  const ret = useOrgRampFlagWithLoading(
    'collection_overview_phase_1',
    RampIdType.OrgId,
    orgId
  );

  return {
    enabled: (!ret.loading && ret.data) || false,
    loading: ret.loading,
  };
};

export const useStatsigGateCollectionOverviewPhase1 = (orgId: string) => {
  const {enabled} = useStatsigGateCollectionOverviewPhase1WithLoading(orgId);
  return enabled;
};

export const useStatsigGateLineageModernizationPhase2WithLoading = (
  orgId: string
): {enabled: boolean; loading: boolean} => {
  const ret = useOrgRampFlagWithLoading(
    'lineage_modernization_phase_2',
    RampIdType.OrgId,
    orgId
  );

  return {
    enabled: (!ret.loading && ret.data) || false,
    loading: ret.loading,
  };
};

export const useStatsigGateLineageModernizationPhase2 = (orgId: string) => {
  const {enabled} = useStatsigGateLineageModernizationPhase2WithLoading(orgId);
  return enabled;
};

export const useRampFlagRunLogsPagination = () => {
  return useUserRampFlag('run_logs_pagination', RampIdType.UserId);
};

export const useRampFlagAndOrFilters = () => {
  const value = useUserRampFlag('and_or_filters', RampIdType.UserId) ?? false;
  setAndOrFiltersEnabled(value);
  return value;
};

export const useRampFlagVideoStreaming = (entityName: string) => {
  const isEntityEnabled = useEntityRampFlag(
    entityName,
    'video_streaming_entity',
    RampIdType.EntityId
  );
  return isEntityEnabled;
};

export const useRampFlagWorkspaceFilterVisibilityUI = () => {
  return (
    useUserRampFlag('workspace_filter_visibility_ui', RampIdType.UserId) ??
    false
  );
};

export const useRampFlagRunsTableCountLabelWording = () => {
  return (
    useUserRampFlag('runs_table_count_label_wording', RampIdType.UserId) ??
    false
  );
};

export const useStatsigGateRegistryUIPhase3 = (orgId: string) => {
  const isOrgEnabled = useOrgIdRampFlag(
    'registry_ui_phase_3',
    RampIdType.OrgId,
    orgId
  );

  return isOrgEnabled || false;
};

export const useRampFlagEntityLessProfilePage = () => {
  return (
    useUserRampFlag('entity_less_profile_page', RampIdType.UserId) ?? false
  );
};

export const useRampFlagUPlotUser = () => {
  return useUserRampFlag('uplot_user', RampIdType.UserId);
};

export const useRampFlagUnlimitedTrackedHoursForAll = () => {
  return useUserRampFlag('unlimited_tracked_hours_for_all', RampIdType.UserId);
};

export const useStatsigGateLinkWeaveToRegistry = () => {
  return useOrgRampFlag('link_weave_prompts_to_registry', RampIdType.OrgId);
};

export const useStatsigGateEnableAccountHomepage = (): {
  enabled: boolean;
  loading: boolean;
} => {
  const results = useOrgRampFlagWithLoading(
    'enable_account_homepage',
    RampIdType.OrgId
  );

  return {
    enabled: (!results.loading && results.data) || false,
    loading: results.loading,
  };
};

export const useStatsigGateEnableHomepageRefresh = (): {
  enabled: boolean;
  loading: boolean;
} => {
  const ret = useUserRampFlagWithLoading(
    'enable_homepage_refresh',
    RampIdType.UserId
  );
  return {
    enabled: (!ret.loading && ret.isEnabled) || false,
    loading: ret.loading,
  };
};

export const useStatsigGateHideStopRunsUI = () => {
  return useUserRampFlag('hide_stop_run_ui', RampIdType.UserId);
};

export const useStatsigGateCrossProjectPinnedRuns = () => {
  return useUserRampFlag('cross_project_pinned_runs', RampIdType.UserId);
};

export const useMaxPinnedRuns = () => {
  const isCrossProjectRunsEnabled = useStatsigGateCrossProjectPinnedRuns();
  return isCrossProjectRunsEnabled ? MAX_PINNED_RUNS : OLD_MAX_PINNED_RUNS;
};

export const useStatsigGateSmartFeatureSettings = (orgName: string) => {
  const {data} = useOrgNameRampFlag(
    orgName,
    'smart_feature_settings',
    RampIdType.OrgId
  );
  return data;
};

/**
 * Hook for sparse panel overrides feature flag.
 * This flag controls whether the new sparse panel overrides system is used
 * (fractional indexing, override tracking, keeping isPanelsAuto=true).
 *
 * The hook also caches the result for synchronous access in Redux reducers.
 *
 * See also: useRampFlagSparseCompression for the compression/persistence flag.
 */
export const useRampFlagSparseOverrides = () => {
  const isUserEnabled = useUserRampFlag(
    'sparse_panel_overrides_user',
    RampIdType.UserId
  );

  const value = isUserEnabled ?? false;

  // Cache the value for synchronous access in reducers
  setSparseOverridesEnabled(value);

  DD_RUM.setGlobalContextProperty('sparse_panel_overrides', value);
  return value;
};

/**
 * Hook for sparse compression feature flag.
 * This flag controls whether auto-generated panels are stripped during
 * compression/save, reducing spec size.
 *
 * This is separate from useRampFlagSparseOverrides for phased rollout:
 * - Phase 1: Overrides ON, Compression OFF (safe rollback)
 * - Phase 2: Overrides ON, Compression ON (full sparse mode)
 *
 * The hook also caches the result for synchronous access in compression logic.
 */
export const useRampFlagSparseCompression = () => {
  const isUserEnabled = useUserRampFlag(
    'sparse_compression_user',
    RampIdType.UserId
  );

  const value = isUserEnabled ?? false;

  // Cache the value for synchronous access in compression logic
  setSparseCompressionEnabled(value);

  DD_RUM.setGlobalContextProperty('sparse_compression', value);
  return value;
};

/**
 * Ramp flag controlling using parallel gzip for delta queries
 */
export const useRampFlagParallelGzip = () => {
  const isOrgEnabled = useOrgRampFlag(
    'enable_parallel_gzip_delta_queries_org',
    RampIdType.OrgId
  );

  const value = isOrgEnabled ?? false;
  DD_RUM.setGlobalContextProperty('parallel_gzip', value);
  setParallelGzipEnabled(value);
  return value;
};

export const useStatsigGateWorkspaceAgent = () => {
  return useUserRampFlag('workspace_agent', RampIdType.UserId);
};

export const useRampFlagEnableWbAgentUsageCharts = (
  orgName: string
): {enabled: boolean; loading: boolean} => {
  const ret = useOrgNameRampFlag(
    orgName,
    'enable_wb_agent_usage_charts',
    RampIdType.OrgId
  );
  return {
    enabled: (!ret.loading && ret.data) || false,
    loading: ret.loading,
  };
};

export const useRampFlagMediaFilesQueryWithUrls = () => {
  return useUserRampFlag('media_files_query_with_urls', RampIdType.UserId);
};

export const useRampFlagSandboxes = (orgId?: string | null) =>
  useOrgIdRampFlag('sandboxes', RampIdType.OrgId, orgId ?? null);

export const useRampFlagInferenceCachedUncachedCSV = () => {
  return useOrgIdRampFlag(
    'inference_csv_cached_uncached_columns',
    RampIdType.OrgId
  );
};

export const useStatsigGateSlurmDaemon = (orgId?: string | null) =>
  useOrgIdRampFlag('wandb-slurm-daemon', RampIdType.OrgId, orgId ?? null);

export const useRampFlagMarimoPanelsUser = () => {
  return useUserRampFlag('marimo_panels_user', RampIdType.UserId);
};

export const useRampFlagMarimoNotebookManagement = (orgId?: string | null) =>
  useOrgIdRampFlag(
    'marimo_notebook_management',
    RampIdType.OrgId,
    orgId ?? null
  );

export const useRampFlagTeamUsageChart = () => {
  return useUserRampFlag('team_usage_chart', RampIdType.UserId);
};

export const useRampFlagRunSelectorPinOnHover = () =>
  useUserRampFlag('run_selector_pin-on-hover', RampIdType.UserId);

export const useStatsigGateEvalTables = () => {
  const isEnabled = useOrgIdRampFlag('eval_tables', RampIdType.OrgId);
  if (envIsServerDeployment) {
    return true;
  }
  return isEnabled;
};

export const useRampFlagShortcutDrawer = () => {
  return useUserRampFlag('shortcut_drawer', RampIdType.UserId) ?? false;
};
